import { type NumberFieldProps, NumberField, get } from '@mfw/components';
import { type FieldError, Controller } from 'react-hook-form';

export interface FormNumberFieldProps extends Omit<NumberFieldProps, 'errors'> {
  withStepButtons?: boolean;
  className?: string;
  name: string;
}

export const FormNumberField = ({
  className,
  fullWidth = true,
  name,
  onChange,
  required = false,
  value,
  withStepButtons = false,
  ...otherProps
}: FormNumberFieldProps) => {
  return (
    <Controller
      render={({ field, formState: { defaultValues, errors, isLoading } }) => {
        const formDefaultValue = value ?? get(defaultValues, name);
        const error = get<FieldError>(errors, name) ?? undefined;

        return (
          <NumberField
            {...field}
            {...otherProps}
            onChange={(e) => {
              const val = e.target.value;
              // `null`, never `undefined`: RHF reads an undefined field value as
              // "no value at all" and hands back the value the field mounted
              // with (`useController` → `useWatch` defaultValue fallback), so an
              // emptied field would refill itself and the digit could not be
              // deleted. `null` is a value, so it clears. Same contract as the
              // numeric FormTextField.
              const numericValue = val === '' ? null : Number(val);
              field.onChange(numericValue);
              if (onChange) {
                onChange(e);
              }
            }}
            loading={otherProps?.loading || isLoading}
            withStepButtons={withStepButtons}
            defaultValue={formDefaultValue}
            errors={error?.message}
            fullWidth={fullWidth}
            className={className}
            value={field.value}
            required={required}
            ref={field.ref}
          />
        );
      }}
      name={name}
    />
  );
};
